import { Box, Card, Stack, Text, Title, Button, CopyButton, Tabs, TextInput, Textarea } from '@mantine/core';
import React from 'react';

import { KeyValueEditor } from '../../../components/KeyValueEditor';
import { Page } from '../../../components/PageLayout';

export const LineUrlGeneratorTab = () => {
    const [params, setParams] = React.useState<Record<string, string>>({});
    const [payload, setPayload] = React.useState<Record<string, string>>({});
    const [activeTab, setActiveTab] = React.useState<string | null>('web');

    const searchParams = React.useMemo(() => {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            searchParams.set(key, value);
        }
        if (Object.keys(payload).length > 0) {
            searchParams.set('payload', JSON.stringify(payload));
        }
        return searchParams;
    }, [params, payload]);

    return (
        <Page>
            <Stack gap="lg">
                <Card withBorder>
                    <Card.Section inheritPadding py="sm">
                        <Title order={4}>URL Parameters</Title>
                    </Card.Section>
                    <Card.Section inheritPadding py="md">
                        <KeyValueEditor onUpdate={setParams} />
                    </Card.Section>
                </Card>
                <Card withBorder>
                    <Card.Section inheritPadding py="sm">
                        <Title order={4}>Analytics Payload</Title>
                    </Card.Section>
                    <Card.Section inheritPadding py="md">
                        <KeyValueEditor onUpdate={setPayload} />
                    </Card.Section>
                </Card>
                <Card withBorder>
                    <Card.Section inheritPadding py="sm">
                        <Title order={4}>Generate URL</Title>
                    </Card.Section>
                    <Card.Section inheritPadding py="md">
                        <Tabs value={activeTab} onChange={(val) => setActiveTab(val)}>
                            <Tabs.List>
                                <Tabs.Tab value="web">Web URL</Tabs.Tab>
                                <Tabs.Tab value="liff">LIFF URL</Tabs.Tab>
                                <Tabs.Tab value="mini">MINI App URL</Tabs.Tab>
                                <Tabs.Tab value="deeplink">LINE app deeplink</Tabs.Tab>
                            </Tabs.List>
                            <Tabs.Panel value="web" pt="md">
                                <WebUrlGenerator searchParams={searchParams} />
                            </Tabs.Panel>
                            <Tabs.Panel value="liff" pt="md">
                                <LiffUrlGenerator searchParams={searchParams} />
                            </Tabs.Panel>
                            <Tabs.Panel value="mini" pt="md">
                                <MiniAppUrlGenerator searchParams={searchParams} />
                            </Tabs.Panel>
                            <Tabs.Panel value="deeplink" pt="md">
                                <LineAppUrlGenerator searchParams={searchParams} />
                            </Tabs.Panel>
                        </Tabs>
                    </Card.Section>
                </Card>
            </Stack>
        </Page>
    );
};

const WebUrlGenerator = ({ searchParams }: { searchParams: URLSearchParams }) => {
    const [baseUrl, setBaseUrl] = React.useState<string>('');

    const generatedUrl = React.useMemo(() => {
        try {
            const url = new URL(baseUrl);
            url.search = searchParams.toString();
            return url.toString();
        } catch {
            return '';
        }
    }, [baseUrl, searchParams]);

    return (
        <Stack gap="lg">
            <TextInput
                label="Base URL"
                placeholder="https://buruburu.app"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.currentTarget.value)}
            />
            <GeneratedUrlTextarea label="Generated URL" url={generatedUrl} placeholder="Enter a base URL to generate" />
        </Stack>
    );
};

const LiffUrlGenerator = ({ searchParams }: { searchParams: URLSearchParams }) => {
    const [liffId, setLiffId] = React.useState<string>('');

    const generatedUrl = React.useMemo(() => {
        if (!liffId) return '';
        try {
            const url = new URL(`https://liff.line.me/${liffId}`);
            url.search = searchParams.toString();
            return url.toString();
        } catch {
            return '';
        }
    }, [liffId, searchParams]);

    return (
        <Stack gap="lg">
            <TextInput
                label="LIFF ID"
                placeholder="1656914823-p4LDgAog"
                value={liffId}
                onChange={(e) => setLiffId(e.currentTarget.value)}
            />
            <GeneratedUrlTextarea
                label="Generated LIFF URL"
                url={generatedUrl}
                placeholder="Enter a LIFF ID to generate"
            />
        </Stack>
    );
};

const MiniAppUrlGenerator = ({ searchParams }: { searchParams: URLSearchParams }) => {
    const [miniAppId, setMiniAppId] = React.useState<string>('');

    const generatedUrl = React.useMemo(() => {
        if (!miniAppId) return '';
        try {
            const url = new URL(`https://miniapp.line.me/${miniAppId}`);
            url.search = searchParams.toString();
            return url.toString();
        } catch {
            return '';
        }
    }, [miniAppId, searchParams]);

    return (
        <Stack gap="lg">
            <TextInput
                label="MINI App ID"
                placeholder="2007326019-vAg8J6Bp"
                value={miniAppId}
                onChange={(e) => setMiniAppId(e.currentTarget.value)}
            />
            <GeneratedUrlTextarea
                label="Generated MINI App URL"
                url={generatedUrl}
                placeholder="Enter a MINI App ID to generate"
            />
        </Stack>
    );
};

const LineAppUrlGenerator = ({ searchParams }: { searchParams: URLSearchParams }) => {
    const [liffOrMiniAppId, setLiffOrMiniAppId] = React.useState<string>('');

    const generatedUrl = React.useMemo(() => {
        if (!liffOrMiniAppId) return '';
        try {
            const url = new URL(`line://app/${liffOrMiniAppId}`);
            url.search = searchParams.toString();
            return url.toString();
        } catch {
            return '';
        }
    }, [liffOrMiniAppId, searchParams]);

    return (
        <Stack gap="lg">
            <TextInput
                label="LIFF ID or MINI App ID"
                placeholder="2007326019-vAg8J6Bp"
                value={liffOrMiniAppId}
                onChange={(e) => setLiffOrMiniAppId(e.currentTarget.value)}
            />
            <GeneratedUrlTextarea
                label="Generated LINE app deeplink"
                url={generatedUrl}
                placeholder="Enter a LINE App ID to generate"
            />
        </Stack>
    );
};

const GeneratedUrlTextarea = ({ label, url, placeholder }: { label: string; url: string; placeholder?: string }) => {
    return (
        <Box pos="relative">
            <Text fw={500} mb={4}>
                {label}
            </Text>
            <Textarea
                rows={3}
                value={url}
                placeholder={placeholder}
                readOnly
                variant="filled"
                styles={{ input: { fontFamily: 'monospace' } }}
                onClick={(e) => {
                    (e.target as HTMLTextAreaElement).select();
                }}
            />
            {url && (
                <CopyButton value={url}>
                    {({ copied, copy }) => (
                        <Button
                            onClick={copy}
                            variant="outline"
                            pos="absolute"
                            style={{ top: 28, right: 8, minWidth: 60 }}
                        >
                            {copied ? 'Copied!' : 'Copy'}
                        </Button>
                    )}
                </CopyButton>
            )}
        </Box>
    );
};
